import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BaseServiceFacade, GenericApiCall } from 'src/app/common/store/types';
import { AppState } from '../types';
import { navigationActions } from './navigation.actions';
import { navigationSelectors } from './navigation.selectors';
import { Menu, Module } from './types';
import { catchError, filter, map, Observable, of, take } from 'rxjs';
import { normalizeRoute } from '../../helper/routehelper';

@Injectable()
export class NavigationService extends BaseServiceFacade {
  private static readonly DEFAULT_LANDING_URL =
    '/Crm/SalesPersons/ProductionDashboard';
  private static readonly FALLBACK_ROUTE = '/page-not-found';

  constructor(store: Store<AppState>) {
    super(store);
  }
  getModules: GenericApiCall<Module[], void> = this.genericApiCall(
    navigationActions.getModules,
    navigationSelectors.getModules,
    true,
  );
  getMenus: GenericApiCall<Menu[], string> = this.genericApiCall(
    navigationActions.getMenus,
    navigationSelectors.getMenus,
    true,
  );

  /**
   * Calcule la page d'accueil de l'utilisateur courant.
   * Si l'utilisateur a accès à l'URL par défaut (présente dans les menus renvoyés par
   * `getModules`), on y va ; sinon on prend la première URL réellement accessible.
   * Source unique de vérité partagée par le login et le guard du root (`path: ''`).
   */
  resolveLandingUrl(): Observable<string> {
    return this.getModules.call().pipe(
      filter((modules) => !!modules && modules.length > 0),
      take(1),
      map((modules) => {
        const url = this.hasAccessTo(
          modules,
          NavigationService.DEFAULT_LANDING_URL,
        )
          ? NavigationService.DEFAULT_LANDING_URL
          : this.firstAccessibleUrl(modules);
        return url;
      }),
    );
  }

  /**
   * Résout la page cible au clic sur un module (`ModuleRedirectGuard`) : la première
   * URL réellement accessible du module, plutôt qu'une redirection en dur non vérifiée.
   * `selfRoute` (le chemin racine gardé, ex. `/Crm`) est exclu des candidats pour ne
   * pas re-déclencher le guard en boucle.
   */
  resolveModuleLandingUrl(
    moduleId: string,
    selfRoute?: string,
  ): Observable<string> {
    return this.getModules.call().pipe(
      filter((modules) => !!modules && modules.length > 0),
      take(1),
      map((modules) => {
        const module = modules.find((m) => m.id === moduleId);
        const url =
          this.firstAccessibleRoute(module, selfRoute) ??
          NavigationService.FALLBACK_ROUTE;
        return url;
      }),
      catchError(() => of(NavigationService.FALLBACK_ROUTE)),
    );
  }

  /** Vrai si `url` figure dans les menus d'au moins un module accessible. */
  private hasAccessTo(modules: Module[], url: string): boolean {
    const target = normalizeRoute(url);
    return modules.some((m) =>
      this.flattenMenuRoutes(m.menus ?? []).some(
        (route) => normalizeRoute(route) === target,
      ),
    );
  }

  /** Première URL accessible parmi tous les modules (1er module qui en a une). */
  private firstAccessibleUrl(modules: Module[]): string {
    for (const module of modules) {
      const url = this.firstAccessibleRoute(module);
      if (url) return url;
    }
    return NavigationService.FALLBACK_ROUTE;
  }

  /**
   * Première route accessible d'un module, en excluant sa propre route racine
   * (ex. `/Crm`) et un éventuel `selfRoute`, pour éviter les boucles de redirection.
   */
  private firstAccessibleRoute(
    module: Module | undefined,
    selfRoute?: string,
  ): string | undefined {
    if (!module) return undefined;
    const excluded = [module.route, selfRoute]
      .filter((route): route is string => !!route)
      .map((route) => normalizeRoute(route));
    return this.flattenMenuRoutes(module.menus ?? []).find(
      (route) => !excluded.includes(normalizeRoute(route)),
    );
  }

  private flattenMenuRoutes(menus: Menu[]): string[] {
    return (menus ?? []).flatMap((menu) => [
      ...(menu.route ? [menu.route] : []),
      ...this.flattenMenuRoutes(menu.children ?? []),
    ]);
  }
}
